/**
 * Tooltip Component
 *
 * Tooltip reutilizavel baseado em shadcn/ui com Radix UI.
 * Suporta diferentes posicoes, delays configuráveis, e conteudo rich text.
 *
 * @since 2025-12-16
 * @updated 2026-04-06 - Migrado para shadcn/ui (Radix Tooltip + cn())
 * @epic INIT-0046 Epic 0002
 */

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

// ==========================================
// TYPES
// ==========================================

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';
type TooltipVariant = 'dark' | 'light';

// ==========================================
// SHADCN PRIMITIVES (exported for compound usage)
// ==========================================

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground',
        'animate-in fade-in-0 zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// ==========================================
// SIMPLE WRAPPER (mantém API existente)
// ==========================================

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: TooltipPlacement;
  /** @deprecated use placement. side is the Radix alias */
  side?: TooltipPlacement;
  variant?: TooltipVariant;
  delay?: number;
  disabled?: boolean;
  className?: string;
  maxWidth?: number;
}

const variantContentClass: Record<TooltipVariant, string> = {
  dark: 'bg-gray-900 text-white',
  light: 'bg-white text-gray-900 border border-gray-200 shadow-lg',
};

export function Tooltip({
  children,
  content,
  placement = 'top',
  side,
  variant = 'dark',
  delay = 200,
  disabled = false,
  className,
  maxWidth = 250,
}: TooltipProps) {
  const resolvedSide = (side ?? placement) as TooltipPrimitive.TooltipContentProps['side'];

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider delayDuration={delay}>
      <TooltipRoot>
        <TooltipTrigger asChild>
          <span className="inline-flex">{children}</span>
        </TooltipTrigger>
        <TooltipContent
          side={resolvedSide}
          className={cn(variantContentClass[variant], className)}
          style={{ maxWidth }}
        >
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}

// ==========================================
// INFO TOOLTIP (Preset)
// ==========================================

interface InfoTooltipProps {
  content: React.ReactNode;
  placement?: TooltipPlacement;
  iconClassName?: string;
}

export function InfoTooltip({ content, placement = 'top', iconClassName }: InfoTooltipProps) {
  return (
    <Tooltip content={content} placement={placement}>
      <span className={cn('inline-flex cursor-help', iconClassName)}>
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </span>
    </Tooltip>
  );
}

// ==========================================
// HELP TOOLTIP (Preset)
// ==========================================

interface HelpTooltipProps {
  content: React.ReactNode;
  placement?: TooltipPlacement;
}

export function HelpTooltip({ content, placement = 'right' }: HelpTooltipProps) {
  return (
    <Tooltip content={content} placement={placement} variant="light">
      <span className="inline-flex cursor-help">
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </span>
    </Tooltip>
  );
}

// ==========================================
// EXPORTS (compound usage)
// ==========================================

export { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent };
